import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GroupDashboard = () => {
  const { user, token } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  
  const [group, setGroup] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [maxSeats, setMaxSeats] = useState(5);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    fetchGroupData();
  }, [token]);

  const fetchGroupData = async () => {
    try {
      setLoading(true);
      const [groupRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/groups/my-group`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null),
        axios.get(`${API_URL}/api/groups/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      ]);

      if (groupRes?.data) {
        setGroup(groupRes.data);
      }
      if (statsRes?.data) {
        setStats(statsRes.data);
      }
    } catch (err) {
      // User might not have a group yet
      setGroup(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API_URL}/api/groups/create`,
        { name: groupName, max_seats: maxSeats },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGroup(response.data);
      setShowCreateModal(false);
      fetchGroupData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleCopyInviteCode = () => {
    navigator.clipboard.writeText(group?.invite_code);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleRegenerateCode = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/groups/regenerate-code`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGroup(prev => ({ ...prev, invite_code: response.data.invite_code }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to regenerate code');
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the group?`)) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/groups/members/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchGroupData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const isAdmin = group?.admin_email === user?.email;
  const seatsRemaining = group ? group.max_seats - group.used_seats : 0;
  const seatsRunningLow = seatsRemaining <= 2 && seatsRemaining >= 0;

  const handleBuyMoreSeats = () => {
    // Redirect to pricing page for PayPal Group plan purchase
    navigate('/pricing');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/dashboard" className="flex items-center">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#b8e1f5]">
                <img src="/logo.png" alt="Study Quiz" className="w-10 h-10 object-cover" />
              </div>
              <span className="ml-3 text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Study Quiz
              </span>
            </Link>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {isDarkMode ? (
                  <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Dashboard
        </button>

        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 rounded-lg">
            {error}
            <button onClick={() => setError('')} className="ml-4 text-red-500">×</button>
          </div>
        )}

        {!group ? (
          /* No Group - Show Options */
          <div className="text-center py-16">
            <div className="text-6xl mb-6">🏫</div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Group Plan Management
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-md mx-auto">
              Create a group for your school or institution, or join an existing group with an invite code.
            </p>
            
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-all"
              >
                Create a Group
              </button>
              <button
                onClick={() => navigate('/join-group')}
                className="px-6 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
              >
                Join with Code
              </button>
            </div>
          </div>
        ) : (
          /* Group Dashboard */
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-3xl font-bold mb-2">{group.name}</h1>
                  <p className="opacity-90">
                    {isAdmin ? 'You are the admin of this group' : `Admin: ${group.admin_name}`}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold">{group.used_seats}/{group.max_seats}</div>
                  <div className="text-sm opacity-90">Seats Used</div>
                  {isAdmin && (
                    <button
                      onClick={handleBuyMoreSeats}
                      className="mt-2 px-4 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-all inline-flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Buy More Seats
                    </button>
                  )}
                </div>
              </div>

              {/* Low Seats Warning */}
              {isAdmin && seatsRunningLow && (
                <div className="mt-4 p-3 bg-amber-500/20 border border-amber-400/40 rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="text-sm font-medium">
                      {seatsRemaining === 0 
                        ? "No seats remaining! Buy more to add teachers." 
                        : `Only ${seatsRemaining} seat${seatsRemaining === 1 ? '' : 's'} remaining!`}
                    </span>
                  </div>
                  <button
                    onClick={handleBuyMoreSeats}
                    className="px-4 py-1.5 bg-amber-500 hover:bg-amber-600 rounded-lg text-sm font-bold transition-all"
                  >
                    Upgrade Now
                  </button>
                </div>
              )}

              {isAdmin && (
                <div className="mt-6 p-4 bg-white/10 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Invite Code</p>
                      <p className="text-2xl font-mono font-bold">{group.invite_code}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleCopyInviteCode}
                        className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
                      >
                        {copySuccess ? '✓ Copied!' : 'Copy'}
                      </button>
                      <button
                        onClick={handleRegenerateCode}
                        className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
                      >
                        Regenerate
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.total_teachers}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Teachers</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{stats.total_quizzes}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Quizzes</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400">{stats.total_classes}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Classes</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.total_students}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Students</div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">{stats.average_quiz_score}%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Avg Score</div>
                </div>
              </div>
            )}

            {/* Members Table */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Team Members</h2>
              </div>
              
              {group.members && group.members.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Quizzes</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Classes</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Joined</th>
                        {isAdmin && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {group.members.map((member) => (
                        <tr key={member.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900 dark:text-white">{member.full_name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-300">{member.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-300">{member.quizzes_created}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-300">{member.classes_created}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-300">
                            {new Date(member.joined_at).toLocaleDateString()}
                          </td>
                          {isAdmin && (
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={() => handleRemoveMember(member.user_id, member.full_name)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <p>No team members yet. Share your invite code to add teachers!</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Create Group Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Create a Group</h2>
              
              <form onSubmit={handleCreateGroup}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Group Name *
                  </label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g., Lincoln High School"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Number of Seats (min 5)
                  </label>
                  <input
                    type="number"
                    min="5"
                    value={maxSeats}
                    onChange={(e) => setMaxSeats(parseInt(e.target.value) || 5)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Each seat allows one teacher to join your group
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700"
                  >
                    Create Group
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupDashboard;
